import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { getContacts } from "actions/getContacts";
import { useDebounce } from "hooks/useDebounce";

import Input from "components/common/Input";

import ChatContactsItem from "../ChatContactsItem";

import styles from "./styles.module.scss";

export default function ChatContactsList({ onChatClick, openedEmploeeId, handleChatWindowClose }) {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState("");

    const users = useSelector((state) => state.contacts.items);

    const loader = useRef(null);
    const isFirstTimeLoaded = useRef(false);
    const usersLenght = useRef(users.length);

    const dispatch = useDispatch();

    const debouncedSearchValue = useDebounce(searchValue, 300);

    const handleChange = (event) => {
        setSearchValue(event.target.value);
    };

    useEffect(() => {
        isFirstTimeLoaded.current = false;
        dispatch(getContacts(debouncedSearchValue));
    }, [debouncedSearchValue, dispatch]);

    const handleObserver = useCallback(
        (entries) => {
            const target = entries[0];
            if (target.isIntersecting) {
                dispatch(getContacts(searchValue, users.length));
                isFirstTimeLoaded.current = true;
                usersLenght.current = users.length;
            }
        },
        [dispatch, users, searchValue],
    );

    useEffect(() => {
        const option = {
            root: null,
            rootMargin: "20px",
            threshold: 0,
        };
        const observer = new IntersectionObserver(handleObserver, option);

        if (loader.current && (users.length > usersLenght.current || !isFirstTimeLoaded.current)) {
            observer.observe(loader.current);
        } else {
            observer.disconnect();
        }
        return () => observer.disconnect();
    }, [handleObserver, users]);

    return (
        <div className={styles.contacts}>
            <Input
                wrapperClass={styles.contacts__search}
                onChange={handleChange}
                placeholder={t("dialog.findContacts")}
            />
            <div className={styles.contacts__list}>
                {users.length > 0 ? (
                    users.map((el, index) => (
                        <ChatContactsItem
                            contact={el}
                            key={index}
                            onChatClick={onChatClick}
                            openedEmploeeId={openedEmploeeId}
                            handleChatWindowClose={handleChatWindowClose}
                        />
                    ))
                ) : (
                    <div className={styles.contacts__empty}>
                        {searchValue ? t("dialog.contactNotFound") : t("dialog.noContacts")}
                    </div>
                )}
                <div ref={loader} className={styles.contacts__end} />
            </div>
        </div>
    );
}
